/**
 * AJ 2026-09-30: "We need to see an 'updated @...' for each tab somewhere on
 * the page." Every client-visible tab says when its data was last refreshed,
 * read from the sync record (public.sync_log), never from the browser clock.
 *
 * This module is the one place that says which record a tab's stamp comes
 * from, and how the stamp reads. Plain .mjs so node:test drives it with no
 * Supabase and no TSX loader (scripts/tab-freshness.test.mjs); the read and
 * the React hook live in tabFreshnessData.ts.
 *
 * "Updated @" for a tab = the newest completed_at with status success on
 * sync_log for the entity the tab reads, shown in the viewer's local time.
 * Two exceptions, both named here:
 *   - Incidents has no sync_log entity: its stamp is the newest
 *     safety_incident.snapshot_taken_on for the tenant (a date, no time).
 *   - DOT Audit has no data feed and its content module carries no edition
 *     date, so it shows no stamp.
 *
 * The sidebar freshness badge (FreshnessBadge.tsx) resolves the SAME source
 * for the route on screen through entityForTabRoute, and reads through the
 * same cached promise, so the badge and the chip cannot disagree.
 */

/** Pseudo entity key: the newest safety_incident.snapshot_taken_on. */
export const INCIDENT_SNAPSHOT = "SAFETY_INCIDENT_SNAPSHOT";

/** Pseudo entity key: the newest successful sync of any entity. */
export const ANY_ENTITY = "*";

/**
 * Report port id -> the entity its page reads. null = no data feed, no stamp.
 * Frozen and exhaustive over reports/registry.mjs (the test checks both ways).
 */
export const REPORT_ENTITY = Object.freeze({
  ar_aging: "AR_CURRENT",
  containers: "SERVICE_EVENT",
  disposal: "DISPOSAL",
  dot_audit: null,
  incidents: INCIDENT_SNAPSHOT,
  price_increase: "INVOICE",
  revenue: "INVOICE",
  ro_efficiency: "SERVICE_EVENT",
  route_profitability: "SERVICE_EVENT",
  sl_cart: "SERVICE_EVENT",
});

/** The entity a report port reads, or null when it has no feed / no port. */
export function entityForReport(reportId) {
  if (typeof reportId !== "string") return null;
  return Object.prototype.hasOwnProperty.call(REPORT_ENTITY, reportId)
    ? REPORT_ENTITY[reportId]
    : null;
}

/**
 * The Executive page's entity. `executive_page: derived` (Best Disposal, and
 * Delta from AJ 2026-09-30, PR #739) renders public.exec_derived; every other
 * tenant renders its own workbook (exec_kpi).
 */
export function executiveEntity(executivePage) {
  return executivePage === "derived" ? "EXEC_DERIVED" : "EXEC_KPI";
}

/**
 * Manifest widget source (the Zoho display name, as queries.ts reads it) ->
 * the canonical entity key the pipeline stamps on sync_log. CQT views and
 * reports have no entity of their own and are skipped.
 */
export const SOURCE_ENTITY = Object.freeze({
  "Meridian - Service Events": "SERVICE_EVENT",
  "Meridian - KPI Summary": "KPI_SUMMARY",
  "Meridian - Financial Health": "FINANCIAL_HEALTH",
  "Meridian - Route KPIs": "ROUTE_KPIS",
  "Meridian - AR Current": "AR_CURRENT",
  "Meridian - AR Transactions": "AR_TRANSACTIONS",
  "Meridian - Site Contracts": "SITE_CONTRACTS",
  "Meridian - Top AR Accounts": "TOP_AR_ACCOUNTS",
  "Meridian - Contract Expirations": "CONTRACT_EXPIRATIONS",
  "Meridian - Audit Findings": "AUDIT_FINDING",
  "Meridian - Insights": "INSIGHT",
  "Meridian - Customers": "CUSTOMER",
  "Meridian - Assets": "ASSET",
  "Meridian - Disposal": "DISPOSAL",
});

/** A widget source's entity: the display name, or a snake_case table name. */
export function entityForSource(source) {
  if (!source || source.kind !== "table" || typeof source.name !== "string") return null;
  if (Object.prototype.hasOwnProperty.call(SOURCE_ENTITY, source.name)) {
    return SOURCE_ENTITY[source.name];
  }
  return /^[a-z][a-z0-9_]*$/.test(source.name) ? source.name.toUpperCase() : null;
}

/**
 * A manifest-driven tab's entity: the one most of its widgets read (ties go
 * to the first seen, in manifest order). null when no widget reads a table.
 */
export function primaryEntityForWidgets(widgets) {
  const counts = new Map();
  for (const w of widgets ?? []) {
    const entity = entityForSource(w?.source);
    if (entity) counts.set(entity, (counts.get(entity) ?? 0) + 1);
  }
  let best = null;
  let bestCount = 0;
  for (const [entity, count] of counts) {
    if (count > bestCount) {
      best = entity;
      bestCount = count;
    }
  }
  return best;
}

/**
 * The entity behind a dashboard route segment (`/<slug>/<segment>`):
 *   - "Executive" -> executiveEntity(manifest.executive_page)
 *   - a tab with a registered report -> REPORT_ENTITY (null for DOT Audit)
 *   - any other manifest tab -> primaryEntityForWidgets(its widgets)
 *   - anything else (Settings, Insights, ...) -> undefined: not a data tab.
 * null and undefined differ on purpose: null is "a tab with no feed", which
 * shows no chip; undefined lets the sidebar badge fall back to ANY_ENTITY.
 */
export function entityForTabRoute(manifest, segment) {
  if (!manifest || typeof segment !== "string" || segment.length === 0) return undefined;
  const tabs = manifest.tabs ?? [];
  const tab = tabs.find((t) => (t.id ?? t.label) === segment);
  if (segment === "Executive" && !tab) return executiveEntity(manifest.executive_page);
  if (!tab) return undefined;
  if (typeof tab.report === "string" && tab.report in REPORT_ENTITY) {
    return entityForReport(tab.report);
  }
  const widgets = (manifest.widgets ?? []).filter((w) => w.tab === segment);
  return primaryEntityForWidgets(widgets);
}

/** "2026-09-28" -> that calendar day at local midnight (not UTC midnight). */
export function parseDateOnly(value) {
  if (typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A timestamptz string -> Date, or null when unparseable. */
export function parseTimestamp(value) {
  if (typeof value !== "string" || value.length === 0) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Newer ICU (Chrome 110+, some Node builds) puts U+202F / U+00A0 before
 * "AM"/"PM". The stamp reads "4:52 PM" with a plain space everywhere.
 */
function plainSpaces(text) {
  return text.replace(/[\u202f\u00a0]/g, " ");
}

function dayKey(date, timeZone) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * The time part of the stamp, in the viewer's zone (or `opts.timeZone`):
 *   today              -> "4:52 PM"
 *   another day        -> "Sep 29, 4:52 PM"
 *   another year       -> "Sep 29, 2025, 4:52 PM"
 *   dateOnly (a date)  -> "Sep 28" (no time exists, so none is shown)
 */
export function formatUpdatedLabel(at, opts = {}) {
  const { now = new Date(), timeZone, dateOnly = false } = opts;
  const locale = opts.locale ?? "en-US";
  const sameYear =
    dayKey(at, dateOnly ? undefined : timeZone).slice(0, 4) ===
    dayKey(now, dateOnly ? undefined : timeZone).slice(0, 4);
  if (dateOnly) {
    return plainSpaces(
      at.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
        ...(sameYear ? {} : { year: "numeric" }),
      }),
    );
  }
  const time = at.toLocaleTimeString(locale, { timeZone, hour: "numeric", minute: "2-digit" });
  if (dayKey(at, timeZone) === dayKey(now, timeZone)) return plainSpaces(time);
  const date = at.toLocaleDateString(locale, {
    timeZone,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  return plainSpaces(`${date}, ${time}`);
}

/** The chip's full text: "Updated @ 4:52 PM". */
export function updatedAtText(label) {
  return `Updated @ ${label}`;
}

/** The hover title: the full timestamp and the entity it came from. */
export function updatedAtTitle(at, entity, opts = {}) {
  const { timeZone, dateOnly = false } = opts;
  const locale = opts.locale ?? "en-US";
  if (dateOnly || entity === INCIDENT_SNAPSHOT) {
    const day = at.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return plainSpaces(`Newest safety_incident snapshot: ${day}`);
  }
  const full = at.toLocaleString(locale, {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
  return plainSpaces(`Last successful ${entity} sync: ${full}`);
}

/**
 * The stamp from a read's rows. sync_log rows: the newest completed_at among
 * status success (the query already filters and orders; this is the backstop
 * so a mis-shaped answer can never show a failed run's time). Incident rows:
 * the newest snapshot_taken_on. Returns { at, entity, dateOnly } or null.
 */
export function stampFromRows(entityKey, rows) {
  if (!Array.isArray(rows)) return null;
  if (entityKey === INCIDENT_SNAPSHOT) {
    let best = null;
    for (const r of rows) {
      const d = parseDateOnly(r?.snapshot_taken_on);
      if (d && (!best || d > best)) best = d;
    }
    return best ? { at: best, entity: INCIDENT_SNAPSHOT, dateOnly: true } : null;
  }
  let best = null;
  for (const r of rows) {
    if (r?.status !== undefined && r.status !== "success") continue;
    if (entityKey !== ANY_ENTITY && r?.entity_key !== undefined && r.entity_key !== entityKey) {
      continue;
    }
    const d = parseTimestamp(r?.completed_at);
    if (d && (!best || d > best.at)) {
      best = { at: d, entity: entityKey === ANY_ENTITY ? (r.entity_key ?? ANY_ENTITY) : entityKey };
    }
  }
  return best ? { ...best, dateOnly: false } : null;
}

/**
 * 2026-10-05: a sync is not new data. The twice-daily refresh re-pushes every
 * entity whether or not a new source file arrived, so from 2026-10-03 to
 * 2026-10-05, while every Navusoft export for Delta was refused, sync_log
 * still showed SERVICE_EVENT synced at 06:23 and 18:22 each day and the chip
 * read "Updated @ 6:23 AM" over two-day-old data. For an entity fed by source
 * files, the stamp is the EARLIER of the newest successful sync and the
 * newest source file the pipeline loaded (public.uploads processed_at, read
 * in tabFreshnessData.ts): the data on screen is no newer than either. The
 * pipeline's freshness watcher (alerts/freshness.py) uses the same rule.
 *
 * `stamp` is stampFromRows' answer; `sourceAt` a Date or null (no file ever
 * loaded for it, or the read failed: the sync stamp stands alone).
 */
export function combineSourceStamp(stamp, sourceAt) {
  if (!stamp || !(stamp.at instanceof Date)) return stamp ?? null;
  if (!(sourceAt instanceof Date) || Number.isNaN(sourceAt.getTime())) return stamp;
  return sourceAt < stamp.at ? { ...stamp, at: sourceAt } : stamp;
}

/**
 * AJ 2026-09-30: "Delta is expecting updates every hour for this report." A
 * report tab left open refreshes itself when a new sync lands.
 *
 * Poll cadence: one sync_log read per open tab every LIVE_POLL_MS while the
 * tab is visible. Hidden tabs do not poll; coming back (visibilitychange to
 * visible, or window focus) polls at once and restarts the interval.
 */
export const LIVE_POLL_MS = 120_000;

/** How long the chip reads "Updated just now" after a swap. */
export const JUST_UPDATED_MS = 6_000;

/** Back-to-back wake events (focus + visibilitychange) cost one read. */
export const LIVE_MIN_GAP_MS = 5_000;

/** True when `next` is a newer stamp than `base` (null base: any stamp is). */
export function stampAdvanced(base, next) {
  if (!next || !(next.at instanceof Date)) return false;
  if (!base || !(base.at instanceof Date)) return true;
  return next.at.getTime() > base.at.getTime();
}

/**
 * The live refresh loop behind useLiveReport, free of React and Supabase so
 * node:test drives it with mocked timers.
 *
 *   baseline   the stamp the page's data was loaded under (null: none yet)
 *   poll()     -> Promise<stamp | null>: one fresh sync_log read
 *   refetch()  -> Promise: invalidate the report's cache and re-read it; the
 *              new data is swapped in only when this resolves
 *   isVisible  () -> boolean (document.visibilityState === "visible")
 *   onCommit(stamp)       the refetch landed: the chip moves to `stamp`
 *   onJustUpdated(bool)   the quiet "Updated just now" window opens / closes
 *
 * A poll or refetch failure changes nothing: the old data and the old time
 * stay, and the next poll tries again (the stamp is still ahead).
 */
export function createLiveRefresh(opts) {
  const {
    poll,
    refetch,
    isVisible = () => true,
    onCommit = () => {},
    onJustUpdated = () => {},
    intervalMs = LIVE_POLL_MS,
    justUpdatedMs = JUST_UPDATED_MS,
    minGapMs = LIVE_MIN_GAP_MS,
    now = () => Date.now(),
  } = opts;
  let baseline = opts.baseline ?? null;
  let timer = null;
  let justTimer = null;
  let inFlight = false;
  let lastPollAt = -Infinity;
  let stopped = false;

  function arm() {
    if (timer === null && !stopped) timer = setInterval(() => void tick(false), intervalMs);
  }
  function disarm() {
    if (timer !== null) clearInterval(timer);
    timer = null;
  }

  async function tick(wake) {
    if (stopped || inFlight || !isVisible()) return false;
    if (wake && now() - lastPollAt < minGapMs) return false;
    inFlight = true;
    lastPollAt = now();
    try {
      let next;
      try {
        next = await poll();
      } catch {
        return false;
      }
      if (stopped || !stampAdvanced(baseline, next)) return false;
      try {
        await refetch();
      } catch {
        return false;
      }
      if (stopped) return false;
      baseline = next;
      onCommit(next);
      if (justTimer !== null) clearTimeout(justTimer);
      onJustUpdated(true);
      justTimer = setTimeout(() => {
        justTimer = null;
        if (!stopped) onJustUpdated(false);
      }, justUpdatedMs);
      return true;
    } finally {
      inFlight = false;
    }
  }

  function wake() {
    if (stopped) return Promise.resolve(false);
    if (!isVisible()) {
      disarm();
      return Promise.resolve(false);
    }
    disarm();
    arm();
    return tick(true);
  }

  return {
    start() {
      stopped = false;
      if (isVisible()) arm();
    },
    stop() {
      stopped = true;
      disarm();
      if (justTimer !== null) clearTimeout(justTimer);
      justTimer = null;
    },
    /** document visibilitychange: pause when hidden, poll now when shown. */
    onVisibilityChange: wake,
    /** window focus: poll now. */
    onFocus: wake,
    /** Poll now regardless of the wake gap (the stamp cache moved ahead). */
    pollNow: () => tick(false),
    /** Adopt a newer baseline without a refetch (another view already did). */
    setBaseline(stamp) {
      baseline = stamp;
    },
    get baseline() {
      return baseline;
    },
    get polling() {
      return timer !== null;
    },
  };
}
